import React, { useMemo } from 'react';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import { Trash } from 'tabler-icons-react';
import { useCalidad } from './CalidadContext';

const FILTRO = createFilterOptions({ ignoreAccents: true, ignoreCase: true, trim: true, limit: 60 });

function porCodigo(lista, codigo) {
    if (codigo === '' || codigo === null || codigo === undefined) return null;
    return lista.find((o) => o.codigo === codigo) || null;
}

// Una línea del reporte: Parte + Defecto (en cascada) + causa raíz + notas +
// eliminar. Componente controlado puro — todo el estado vive en
// CalidadFormModal, aquí solo se traduce a Autocomplete/TextField.
export default function CalidadLineaDefecto({ numero, linea, errores, onCampo, onEliminar, permitirEliminar }) {
    const { catalogoPartes, catalogoCausas, defectosDeParte } = useCalidad();

    const opcionesDefecto = useMemo(() => defectosDeParte(linea.parte), [defectosDeParte, linea.parte]);
    const parteObj = useMemo(() => porCodigo(catalogoPartes, linea.parte), [catalogoPartes, linea.parte]);
    const defectoObj = useMemo(() => porCodigo(opcionesDefecto, linea.defecto), [opcionesDefecto, linea.defecto]);
    const causaObj = useMemo(() => porCodigo(catalogoCausas, linea.causaRaiz), [catalogoCausas, linea.causaRaiz]);

    const handleParteChange = (e, val) => {
        onCampo('parte', val ? val.codigo : '');
        // Solo se limpia el defecto si dejó de ser válido para la parte nueva.
        const permitidos = defectosDeParte(val ? val.codigo : '');
        if (linea.defecto !== '' && !permitidos.some((d) => d.codigo === linea.defecto)) {
            onCampo('defecto', '');
        }
    };

    return (
        <div
            id={`calidad-linea-${linea.uid}`}
            className={`calidad-linea-card${errores ? ' calidad-linea-card--error' : ''}`}
        >
            <div className="calidad-linea-header">
                <span className="calidad-linea-num">{numero}</span>
                {permitirEliminar && (
                    <IconButton size="small" className="calidad-linea-remove" onClick={onEliminar} aria-label="Eliminar línea">
                        <Trash size={16} />
                    </IconButton>
                )}
            </div>

            <div className="calidad-linea-grid">
                <Autocomplete
                    options={catalogoPartes}
                    value={parteObj}
                    onChange={handleParteChange}
                    getOptionLabel={(o) => o.label}
                    getOptionSelected={(o, v) => o.codigo === v.codigo}
                    filterOptions={FILTRO}
                    openOnFocus
                    autoHighlight
                    clearOnEscape
                    blurOnSelect="touch"
                    noOptionsText="Sin coincidencias"
                    classes={{ paper: 'calidad-ac-paper', option: 'calidad-ac-option' }}
                    renderInput={(p) => (
                        <TextField {...p} label="Parte *" error={!!errores?.parte} helperText={errores?.parte} />
                    )}
                />

                <Autocomplete
                    options={opcionesDefecto}
                    value={defectoObj}
                    onChange={(e, val) => onCampo('defecto', val ? val.codigo : '')}
                    getOptionLabel={(o) => o.label}
                    getOptionSelected={(o, v) => o.codigo === v.codigo}
                    filterOptions={FILTRO}
                    openOnFocus
                    autoHighlight
                    clearOnEscape
                    blurOnSelect="touch"
                    disabled={catalogoPartes.length > 0 && !linea.parte}
                    noOptionsText="Sin coincidencias"
                    classes={{ paper: 'calidad-ac-paper', option: 'calidad-ac-option' }}
                    renderInput={(p) => (
                        <TextField
                            {...p}
                            label="Tipo de defecto *"
                            error={!!errores?.defecto}
                            helperText={
                                errores?.defecto ||
                                (catalogoPartes.length > 0 && !linea.parte ? 'Elige una parte primero' : '')
                            }
                        />
                    )}
                />
            </div>

            <div className="calidad-linea-grid">
                <Autocomplete
                    options={catalogoCausas}
                    value={causaObj}
                    onChange={(e, val) => onCampo('causaRaiz', val ? val.codigo : '')}
                    getOptionLabel={(o) => o.label}
                    getOptionSelected={(o, v) => o.codigo === v.codigo}
                    filterOptions={FILTRO}
                    openOnFocus
                    autoHighlight
                    clearOnEscape
                    blurOnSelect="touch"
                    noOptionsText="Sin coincidencias"
                    classes={{ paper: 'calidad-ac-paper', option: 'calidad-ac-option' }}
                    renderInput={(p) => <TextField {...p} label="Causa raíz (opcional)" />}
                />

                <TextField
                    label="Observaciones (opcional)"
                    value={linea.notas}
                    onChange={(e) => onCampo('notas', e.target.value)}
                    fullWidth
                    inputProps={{ maxLength: 500 }}
                    placeholder="Descripción adicional del defecto..."
                />
            </div>

            {errores?.duplicado && <p className="calidad-linea-dup">Ya hay otra línea con esta misma combinación</p>}
        </div>
    );
}
